import React from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { Flex, Icon, Typo, theme } from '../../../ui';

const SIDEBAR_WIDTH = 260;

const SidebarContainer = styled.div`
  width: ${SIDEBAR_WIDTH}px;
  min-width: ${SIDEBAR_WIDTH}px;
  background: ${theme.colors.NEU_0};
  border-right: 1px solid ${theme.colors.NEU_100};
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  flex-shrink: 0;
`;

const SidebarHeader = styled.div`
  padding: 16px;
  border-bottom: 1px solid ${theme.colors.NEU_100};
`;

const SectionList = styled.div`
  flex: 1;
  padding: 8px;
`;

const SectionItem = styled.button`
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 10px 12px;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  text-align: left;
  font-size: 14px;
  transition: all 0.15s ease;
  background: ${(props) => (props.$active ? theme.colors.PRIMARY_EXTRA_LIGHT : 'transparent')};
  color: ${(props) => (props.$active ? theme.colors.PRIMARY : theme.colors.NEU_PRIMARY)};
  font-weight: ${(props) => (props.$active ? 600 : 400)};

  &:hover {
    background: ${(props) => (props.$active ? theme.colors.PRIMARY_EXTRA_LIGHT : theme.colors.NEU_50)};
  }
`;

const SectionIcon = styled.span`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${(props) => (props.$active ? theme.colors.PRIMARY : theme.colors.NEU_300)};
  flex-shrink: 0;
`;

const SidebarFooter = styled.div`
  padding: 12px 16px;
  border-top: 1px solid ${theme.colors.NEU_100};
`;

const MemberAvatarStack = styled(Flex)`
  padding: 4px 0;
`;

const MemberDot = styled.div`
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: ${theme.colors.PRIMARY_EXTRA_LIGHT};
  border: 2px solid ${theme.colors.NEU_0};
  display: flex;
  align-items: center;
  justify-content: center;
  margin-left: -6px;
  font-size: 11px;
  color: ${theme.colors.PRIMARY};
  font-weight: 600;

  &:first-child {
    margin-left: 0;
  }
`;

const ClassroomSidebar = ({ sections = [], activeSectionId, onSelectSection, members = [] }) => {
  const { t } = useTranslation();

  return (
    <SidebarContainer>
      <SidebarHeader>
        <Typo.SmallText color={theme.colors.NEU_600}>
          {t('classroom.channels', 'UNITS')}
        </Typo.SmallText>
      </SidebarHeader>

      <SectionList>
        {sections.map((section) => (
          <SectionItem
            key={section.id}
            $active={section.id === activeSectionId}
            onClick={() => onSelectSection(section.id)}
          >
            <SectionIcon $active={section.id === activeSectionId} />
            {section.name}
          </SectionItem>
        ))}
        {sections.length === 0 && (
          <Flex direction='column' align='center' gap='8' style={{ padding: '20px 0' }}>
            <Typo.SmallText color={theme.colors.NEU_300}>
              {t('classroom.noSections', 'No units yet')}
            </Typo.SmallText>
          </Flex>
        )}
      </SectionList>

      <SidebarFooter>
        <Flex direction='column' gap='8'>
          <Typo.SmallText color={theme.colors.NEU_600}>
            {t('classroom.members', 'MEMBERS')} ({members.length})
          </Typo.SmallText>
          <MemberAvatarStack align='center'>
            {members.slice(0, 6).map((member, idx) => {
              const initial = (member.name || member.displayName || member.firstName || '?')[0]?.toUpperCase();
              return (
                <MemberDot key={member.id || idx} title={member.name || member.displayName}>
                  {initial}
                </MemberDot>
              );
            })}
            {members.length > 6 && (
              <MemberDot>+{members.length - 6}</MemberDot>
            )}
          </MemberAvatarStack>
        </Flex>
      </SidebarFooter>
    </SidebarContainer>
  );
};

export default ClassroomSidebar;
